import { Loader2, FileText } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/shared/ui/empty';
import { Skeleton } from '@/shared/ui/skeleton';
import { UserCertificate } from '@/entities/user/api/get-users-me-certificates';
import { useRouter } from '@tanstack/react-router';

export interface ProfileCertificate {
  id: string;
  course: {
    title: string;
  };
  created_at: string;
}

export interface CertificatesSectionProps {
  certificates: ProfileCertificate[] | UserCertificate[];
  isLoading: boolean;
  errorMessage?: string;
  onDownload: (certificateId: string) => Promise<void> | void;
  isDownloading: boolean;
}

const formatDate = (input?: string | null) => {
  if (!input) {
    return '-';
  }

  const date = new Date(input);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

export const CertificatesSection = ({
  certificates,
  isLoading,
  errorMessage,
  onDownload,
  isDownloading,
}: CertificatesSectionProps) => {
  const router = useRouter();
  if (isLoading) {
    return (
      <div className="grid gap-4">
        {Array.from({ length: 2 }).map((_, index) => (
          <Card
            key={index}
            className="overflow-hidden border-border/60 shadow-sm"
          >
            <CardContent className="space-y-4 p-5">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-10 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Unable to load certificates</AlertTitle>
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  if (!certificates.length) {
    return (
      <Card className="border-dashed border-border/60 bg-muted/10 shadow-none">
        <CardContent className="px-6 py-16 text-center sm:px-10">
          <Empty className="border-none p-0">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileText className="h-8 w-8" />
              </EmptyMedia>
              <EmptyTitle>No certificates yet</EmptyTitle>
              <EmptyDescription>
                Certificates will appear here once they have been issued.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      {certificates.map((certificate) => (
        <Card
          key={certificate.id}
          className="overflow-hidden border-border/60 shadow-sm"
          onClick={() => router.navigate({ to: '/certificates' })}
        >
          <CardHeader className="flex flex-col gap-3 border-b border-border/60 bg-muted/10 p-4">
            <div>
              <CardTitle className="text-base font-semibold leading-relaxed">
                {certificate.course.title}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground/80">
                Issued on {formatDate(certificate.created_at)}
              </CardDescription>
            </div>
          </CardHeader>
          <CardFooter className="flex justify-end gap-2 border-t border-border/60 bg-muted/20 p-4">
            <Button
              type="button"
              variant="outline"
              className="gap-2 rounded-full px-5 py-3 text-sm font-semibold"
              disabled={isDownloading}
              onClick={() => onDownload(certificate.id)}
              aria-label={`Download ${certificate.course.title} certificate`}
            >
              {isDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
              Download
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};
